import { EditIcon } from "@xola/icons";
import { Badge, Input, Label, Table } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React from "react";
import { Merchandise, MerchandiseCatalogItem, MerchandiseCatalogItemStatus } from "types";
import { CatalogItemImage } from "@/components/Common/CatalogItemImage";
import { ProductImage } from "@/components/Common/ProductImage";
import { CurrencyInput } from "@/components/Input/CurrencyInput";
import { isInventoryTracked } from "@/models/Merchandise";

interface SingleItemValues {
    sku: string;
    barcode: string;
    price: number | undefined;
}

interface SingleItemInventoryTableProps {
    values: SingleItemValues;
    catalogItem: MerchandiseCatalogItem;
    merchandise: Merchandise;
    setFieldValue: (field: string, value?: string | number) => void;
    errors?: any;
    onEdit: (catalogItemId: string) => void;
    onSkuChange: (value: string) => void;
    onBarcodeChange: (value: string) => void;
}

export const SingleItemInventoryTable = ({
    values,
    catalogItem,
    merchandise,
    setFieldValue,
    errors,
    onEdit,
    onSkuChange,
    onBarcodeChange,
}: SingleItemInventoryTableProps) => {
    const hasCatalogPhoto = !!catalogItem.photo;
    const isDraftCatalogItem = catalogItem.status === MerchandiseCatalogItemStatus.DRAFT;
    const isTracked = isInventoryTracked(merchandise);

    return (
        <div className="rounded-lg">
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Name</Table.Header>
                        <Table.Header>SKU</Table.Header>
                        <Table.Header>Barcode</Table.Header>
                        <Table.Header>Price</Table.Header>
                        {isTracked && <Table.Header>Inventory</Table.Header>}
                        <Table.Header />
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    <Table.Row>
                        <Table.Cell data-testid="variant-item-name">
                            <div className="flex items-center space-x-3">
                                {hasCatalogPhoto ? (
                                    <CatalogItemImage catalogItem={catalogItem} size="small" />
                                ) : (
                                    <ProductImage product={merchandise} size="small" />
                                )}
                                <div className="flex flex-col">
                                    <span className="whitespace-pre-wrap break-all">{merchandise.name}</span>
                                    {isDraftCatalogItem && (
                                        <div className="mt-1">
                                            <Badge color="primary" size="small">
                                                Draft
                                            </Badge>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Table.Cell>
                        <Table.Cell data-testid="variant-item-sku">
                            <Input
                                value={values.sku}
                                placeholder="Add SKU"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSkuChange(e.target.value)}
                            />
                        </Table.Cell>
                        <Table.Cell data-testid="variant-item-barcode">
                            <Input
                                value={values.barcode}
                                placeholder="Add Barcode"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onBarcodeChange(e.target.value)}
                            />
                        </Table.Cell>
                        <Table.Cell data-testid="variant-item-price">
                            <div className="w-32 space-y-1">
                                <CurrencyInput
                                    min={0}
                                    isError={!!errors?.price}
                                    value={values.price}
                                    onValueChange={({ floatValue }: { floatValue?: number }) =>
                                        setFieldValue("price", floatValue)
                                    }
                                />
                                {errors?.price && <Label className="!mb-0 text-xs !text-danger">{errors.price}</Label>}
                            </div>
                        </Table.Cell>
                        {isTracked && (
                            <Table.Cell className="text-center">
                                <span className="text-sm">{catalogItem.available ?? 0}</span>
                            </Table.Cell>
                        )}
                        <Table.Cell data-testid="variant-item-edit-button">
                            <SecondaryButton
                                size="small"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (catalogItem.id) {
                                        onEdit(catalogItem.id);
                                    }
                                }}
                            >
                                <EditIcon size="small" />
                            </SecondaryButton>
                        </Table.Cell>
                    </Table.Row>
                </Table.Body>
            </Table>
        </div>
    );
};
